/**
 * The one static check the Logic tab makes.
 *
 * `design/screens/editor-logic.html` draws it as a rule state: an amber "Never
 * runs" chip and, inside the card, **"This rule can never run — Budget is asked
 * after this step."** Story 6.4's criterion asks for that check and that
 * reason, and manifest item B13 records that neither was specified anywhere.
 *
 * IT IS ONE CHECK, DELIBERATELY. A full reachability analysis over a funnel is
 * a graph problem — a rule can also be unreachable because an earlier rule in
 * the same group always wins, or because the step it hangs off is itself only
 * reached by a branch that cannot be taken. Those are real and they are not
 * this. What this catches is the ordering mistake an operator actually makes
 * and cannot see: asking about an answer the respondent has not given yet.
 *
 * The order it reasons about is the STEP order — `funnel.steps` — which is what
 * `logicGroupOf` groups by and what the runtime walks. Nothing here re-derives
 * evaluation order; it only asks where a block sits relative to a rule.
 *
 * Story 6.4.
 */
import { logicGroupOf, type Funnel, type LogicRule } from '@leadgible/schema'

export type Reachability = { ok: true } | { ok: false; reason: string }

const REACHABLE: Reachability = { ok: true }

/** Which step a block lives in, by index. `-1` when it is in none. */
function stepOfBlock(funnel: Funnel, blockId: string): number {
  return funnel.steps.findIndex((step) => step.blocks.includes(blockId))
}

/** What to call a block in a sentence — its question, or its id as a fallback. */
function nameOfBlock(funnel: Funnel, blockId: string): string {
  const block = funnel.blocks.find((b) => b.id === blockId)
  if (!block) return 'That question'
  if ('question' in block && block.question.trim().length > 0) return block.question
  if ('field_key' in block) return block.field_key
  return blockId
}

/**
 * Can this rule ever run?
 *
 * Answers `ok` for everything it cannot prove wrong, which is the right bias
 * for a warning drawn on the operator's own work: a false "never runs" on a
 * rule that does run teaches them to ignore the badge, and then the true one is
 * invisible too.
 */
export function reachability(funnel: Funnel, rule: LogicRule): Reachability {
  const subject = rule.when.subject

  // `extracted` is M2 and `metadata` arrives with the request, so neither has
  // an ordering relationship to a step at all.
  if (subject.kind !== 'answer') return REACHABLE

  const askedAt = stepOfBlock(funnel, subject.block_id)
  const name = nameOfBlock(funnel, subject.block_id)

  // The block was deleted, or never placed in a step. Either way the condition
  // can never be satisfied, and the reason says which.
  if (!funnel.blocks.some((b) => b.id === subject.block_id)) {
    return { ok: false, reason: `${name} no longer exists.` }
  }
  if (askedAt < 0) {
    return { ok: false, reason: `${name} is not in any step.` }
  }

  // An anchorless rule has no position to be compared against — it is its own
  // group (`logicGroupOf`), evaluated on its own terms.
  if (!rule.step_id) return REACHABLE

  const runsAt = funnel.steps.findIndex((step) => step.id === rule.step_id)
  if (runsAt < 0) return { ok: false, reason: 'The step this rule is attached to no longer exists.' }

  // The export's own sentence. Same step is FINE: the answer is given before
  // the step is left, which is when the step's rules are evaluated.
  if (askedAt > runsAt) {
    return { ok: false, reason: `${name} is asked after this step.` }
  }

  return REACHABLE
}

/**
 * The rules, grouped as they are evaluated and numbered as they run.
 *
 * `logicGroupOf` is imported rather than repeated — Story 6.4's criterion,
 * and PRD §3.4's reason for it.
 */
export type RuleGroup = {
  /** The step id, or the synthetic group of an anchorless rule. */
  key: string
  /** The step, when there is one. */
  stepIndex: number
  title: string
  rules: LogicRule[]
}

export function groupedRules(funnel: Funnel): RuleGroup[] {
  const groups = new Map<string, RuleGroup>()

  for (const rule of funnel.logic) {
    const key = logicGroupOf(rule)
    let group = groups.get(key)
    if (!group) {
      const stepIndex = rule.step_id
        ? funnel.steps.findIndex((step) => step.id === rule.step_id)
        : -1
      const step = stepIndex >= 0 ? funnel.steps[stepIndex] : undefined
      group = {
        key,
        stepIndex,
        title: step
          ? `Step ${stepIndex + 1} · ${step.title ?? `Step ${stepIndex + 1}`}`
          : // A rule attached to nothing, or to a step that has been deleted.
            // Named rather than hidden: a rule nobody can see is a rule nobody
            // can fix, and it still evaluates.
            'Not attached to a step',
        rules: [],
      }
      groups.set(key, group)
    }
    group.rules.push(rule)
  }

  // Step order, then the anchorless ones. Within a group, funnel array order —
  // which IS run order, and is why the cards can be numbered at all.
  return [...groups.values()].sort((a, b) => {
    if (a.stepIndex === b.stepIndex) return 0
    if (a.stepIndex < 0) return 1
    if (b.stepIndex < 0) return -1
    return a.stepIndex - b.stepIndex
  })
}
